import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, TrendingUp, FileText, Handshake } from "lucide-react";

/* ======= IMAGES ======= */
import heroSkyline from "@/assets/sections/hero-skyline-v2.jpg?format=webp&w=1200&quality=70";
import aboutBoardroom from "@/assets/sections/about-boardroom-v2.jpg?format=webp&w=600&quality=75";
import ctaHandshake from "@/assets/sections/cta-handshake-v2.jpg?format=webp&w=1200&quality=70";

/* ======= CLIENT LOGOS ======= */
import logoWorldBank from "@/assets/clients/world-bank-group.png?format=webp&w=160";
import logoBID from "@/assets/clients/bid.png?format=webp&w=160";
import logoUPME from "@/assets/clients/upme.png?format=webp&w=160";
import logoTransmilenio from "@/assets/clients/transmilenio.png?format=webp&w=160";
import logoFedecafe from "@/assets/clients/fedecafe.png?format=webp&w=160";
import logoGenserPower from "@/assets/clients/genser-power.png?format=webp&w=160";
import logoPromoEnercol from "@/assets/clients/promo-enercol.png?format=webp&w=160";
import logoSacyr from "@/assets/clients/sacyr.png?format=webp&w=160";
import logoSegurosEstado from "@/assets/clients/seguros-estado.png?format=webp&w=160";
import logoConconcreto from "@/assets/clients/conconcreto.png?format=webp&w=160";
import logoPuertoBahia from "@/assets/clients/puerto-bahia.png?format=webp&w=160";
import logoCanacol from "@/assets/clients/canacol.png?format=webp&w=160";
import logoConsorcioSanJorge from "@/assets/clients/consorcio-san-jorge.png?format=webp&w=160";

import jairoImg from "@/assets/team/jairo-vacca.png?format=webp&w=224&quality=80";
import fernandoImg from "@/assets/team/fernando-sanabria-2.jpeg?format=webp&w=224&quality=80";
import danielImg from "@/assets/team/daniel-barrera.png?format=webp&w=224&quality=80";

/* ======= HOOKS ======= */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const anim = (inView: boolean, delay = 0) =>
  `transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}` +
  (delay ? ` delay-[${delay}ms]` : "");

/* ======= MAIN — 7 SECTIONS ONLY ======= */
export default function CorporateFinanceHome() {
  return (
    <>
      <SectionHero />
      <SectionServices />
      <SectionAbout />
      <SectionTeam />
      <SectionCases />
      <SectionTrackRecord />
      <SectionCTA />
    </>
  );
}

/* =========== SECTION 1 — HERO =========== */
function SectionHero() {
  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden">
      <img
        src={heroSkyline}
        alt="Vista panorámica de distrito financiero"
        className="absolute inset-0 w-full h-full object-cover object-center"
        fetchPriority="high"
        decoding="async"
        width={1920}
        height={1080}
      />
      <div className="absolute inset-0 bg-[hsl(var(--navy-deep)/.65)]" />
      <div className="absolute inset-0 grain-overlay" />
      <div className="container mx-auto px-4 pt-28 pb-16 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <p className="section-label mb-6 animate-fade-in">INVESTMENT BANKING · AMERICAS & EUROPE</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-[4rem] font-bold text-bone leading-tight mb-6 animate-slide-up">
            Soluciones financieras de alto impacto para empresas hispanohablantes
          </h1>
          <p className="font-sans text-lg text-steel max-w-2xl mx-auto mb-10 animate-slide-up stagger-1">
            M&A, levantamiento de capital y CFO as a Service con estándares internacionales. 
            Operamos en español en América y Europa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-slide-up stagger-2">
            <Link to="/contacto" className="btn-gold text-center">
              Agendar diagnóstico gratuito
            </Link>
            <a href="#servicios" className="btn-outline-white text-center">
              Ver servicios
            </a>
          </div>
          {/* Micro-stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-steel animate-fade-in stagger-3">
            <span className="font-mono-stat text-gold font-semibold">+US$10B en transacciones</span>
            <span className="w-px h-4 bg-[hsl(var(--gold)/.4)] hidden sm:block" />
            <span>Americas & Europa</span>
            <span className="w-px h-4 bg-[hsl(var(--gold)/.4)] hidden sm:block" />
            <span>Partner-Led</span>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
        <div className="w-6 h-10 border-2 border-[hsl(var(--gold)/.4)] rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-[hsl(var(--gold))] rounded-full" />
        </div>
      </div>
    </section>
  );
}

/* =========== SECTION 2 — SERVICIOS =========== */
const services = [
  {
    title: "Investment Banking & M&A",
    desc: "Asesoría integral en fusiones, adquisiciones, valoración de empresas y estructuración de capital. Acompañamos la transacción completa.",
    href: "/servicios/investment-banking",
  },
  {
    title: "Corporate Finance & FP&A",
    desc: "Modelos financieros, KPIs, reportes ejecutivos y FP&A para que el dueño tome decisiones con información real.",
    href: "/servicios/corporate-finance",
  },
  {
    title: "CFO as a Service",
    desc: "Dirección financiera y tesorería a tiempo parcial o completo. Un CFO de primer nivel sin el costo de uno full-time.",
    href: "/servicios/cfo-as-a-service",
  },
  {
    title: "Vinculación de Capital o Deuda",
    desc: "Estructuración y gestión de procesos de levantamiento de capital o deuda para financiar crecimiento, adquisiciones o proyectos estratégicos.",
    href: "/servicios/capital-y-deuda",
  },
];

function SectionServices() {
  const { ref, inView } = useInView();
  return (
    <section id="servicios" className="section-padding bg-navy-mid" ref={ref}>
      <div className="container mx-auto px-4">
        <div className={`section-header ${anim(inView)}`}>
          <p className="section-label">SERVICIOS</p>
          <h2 className="section-title text-bone">Todo lo que tu empresa necesita para crecer con control y acceder a capital</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {services.map((s, i) => (
            <div
              key={s.title}
              className={`card-premium p-8 ${anim(inView)}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <h3 className="font-display text-xl font-bold text-bone mb-4">{s.title}</h3>
              <p className="font-sans text-sm text-steel leading-relaxed mb-6">{s.desc}</p>
              <Link to={s.href} className="inline-flex items-center gap-1 text-gold text-sm font-sans font-medium hover:text-[hsl(var(--gold-light))] transition-colors">
                Saber más <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========== SECTION 3 — NOSOTROS =========== */
function SectionAbout() {
  const { ref, inView } = useInView();
  return (
    <section id="nosotros" className="section-padding bg-bone" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-[55%_45%] gap-12 items-start">
          <div className={anim(inView)}>
            <p className="section-label">QUIÉNES SOMOS</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy mb-6">
              Una firma boutique con estándares de banca de inversión internacional
            </h2>
            <p className="font-sans text-steel mb-4 leading-relaxed">
              SYCMA Corporate Finance es una firma de asesoría financiera y banca de inversión enfocada en consultoría, M&A, estructuración y financiación de proyectos vinculando deuda o aportes de capital de riesgo.
            </p>
            <p className="font-sans text-steel mb-4 leading-relaxed">
              Nuestro modelo se sustenta en un profundo conocimiento de mercados estratégicos: energía, gas, petróleo, infraestructura, logística, agroindustria, banca e inmobiliario.
            </p>
            <p className="font-sans text-steel leading-relaxed">
              Operamos con el rigor técnico de las grandes firmas internacionales pero con el diferencial de hacerlo en español, entendiendo la realidad del empresariado hispanohablante en cualquier mercado del mundo.
            </p>
          </div>
          <div className={`transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <img
                src={aboutBoardroom}
                alt="Sala de juntas moderna con vista panorámica"
                className="rounded-lg w-full h-80 object-cover shadow-lg"
                loading="lazy"
                width={500}
                height={320}
              />
              {/* Floating quote card */}
              <div className="absolute -bottom-16 -left-4 bg-navy-deep rounded-xl p-6 max-w-xs shadow-xl border-t-2 border-[hsl(var(--gold))]">
                <p className="font-display text-sm italic text-bone/90 leading-relaxed mb-3">
                  "Somos una firma que está presente en la mente de los empresarios e inversionistas, en la toma de decisiones estratégicas de negocios, con relaciones globales de largo plazo."
                </p>
                <p className="font-sans text-xs text-gold font-medium">
                  Jairo Vacca, Managing Partner
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========== SECTION 4 — EQUIPO SENIOR =========== */
const team = [
  {
    name: "Jairo Vacca",
    role: "Managing Partner",
    location: "Bogotá · Washington D.C.",
    img: jairoImg,
    bullets: [
      "+25 años en banca de inversión · Lloyds TSB, Helm Bank, Davivienda",
      "+US$10B en transacciones de M&A y Corporate Finance",
      "Corporate Finance (Columbia) · MIT · Cornell · Harvard · MBA España",
    ],
    linkedin: "https://www.linkedin.com/in/jairo-o-vacca-b-7591a249",
  },
  {
    name: "Fernando Sanabria",
    role: "Partner",
    location: "Bogotá · Miami",
    img: fernandoImg,
    bullets: [
      "+20 años en finanzas corporativas y banca de inversión",
      "MBA — Instituto Europeo de Posgrados & Escuela de Negocios de Madrid",
      "Experto en valoración, tesorería integral y CFO as a Service",
    ],
    linkedin: "https://www.linkedin.com/in/fernando-oliver-sanabria-ovalle-72142630",
  },
  {
    name: "Daniel Barrera",
    role: "Partner",
    location: "Medellín",
    img: danielImg,
    bullets: [
      "Finanzas — Universidad de Antioquia / EAFIT",
      "+COP $250B en portafolios de inversión · 160+ empresas asesoradas",
      "M&A, capital raising, energías renovables y finanzas corporativas",
    ],
    linkedin: "https://www.linkedin.com/in/daniel-barrera-barrera-6b4a65b8",
  },
];

function SectionTeam() {
  const { ref, inView } = useInView(0.01);
  return (
    <section id="equipo" className="section-padding bg-navy-deep" ref={ref}>
      <div className="container mx-auto px-4">
        <div className={`section-header ${anim(inView)}`}>
          <p className="section-label">EQUIPO SENIOR</p>
          <h2 className="section-title text-bone">Los socios que acompañan cada proceso de principio a fin</h2>
          <p className="section-subtitle mt-4">
            Más de 50 años de experiencia combinada en banca internacional, finanzas corporativas y M&A.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {team.map((member, idx) => (
            <div
              key={member.name}
              className={`bg-navy-mid rounded-xl p-6 border-t-2 border-[hsl(var(--gold))] text-center ${anim(inView)}`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              {/* Photo */}
              <div className="w-44 h-44 mx-auto rounded-full overflow-hidden border-2 border-[hsl(var(--gold))] mb-4 shadow-[0_4px_20px_hsla(39,47%,61%,0.2)]">
                <img src={member.img} alt={member.name} className="w-full h-full object-cover object-top" loading="lazy" width={176} height={176} />
              </div>

              {/* Info */}
              <h3 className="font-display text-xl font-bold text-bone mb-1">{member.name}</h3>
              <p className="font-sans text-gold text-xs font-semibold uppercase tracking-wider mb-1">{member.role}</p>
              <p className="font-sans text-steel text-xs mb-4">{member.location}</p>

              <div className="gold-line mx-auto mb-4" />

              {/* 3 Bullets */}
              <ul className="space-y-2 text-left mb-6">
                {member.bullets.map((bullet) => (
                  <li key={bullet} className="font-sans text-sm text-steel flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gold))] mt-1.5 flex-shrink-0" />
                    {bullet}
                  </li>
                ))}
              </ul>

              {/* LinkedIn */}
              <a
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gold text-sm font-sans font-medium border border-[hsl(var(--gold)/.4)] rounded-lg px-4 py-2 hover:bg-[hsl(var(--gold)/.1)] transition-colors"
              >
                <Linkedin className="h-4 w-4" />
                Ver perfil en LinkedIn
                <ArrowRight className="h-3 w-3" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========== SECTION 5 — CASOS TÍPICOS =========== */
const cases = [
  {
    num: "01",
    title: "Control de Caja",
    icon: TrendingUp,
    text: "Caja desordenada con pagos críticos. → 13-week cash flow + tablero KPI semanal. → Control total del flujo y decisiones con visibilidad.",
  },
  {
    num: "02",
    title: "Preparación para Inversión",
    icon: FileText,
    text: "Sin data room ni métricas sólidas para levantar capital. → Modelo financiero + equity story + data room completo. → Empresa lista para rondas formales de inversión.",
  },
  {
    num: "03",
    title: "M&A",
    icon: Handshake,
    text: "Compra o venta con incertidumbre de valuación y términos. → Valoración + estructuración + negociación de términos. → Transacción ordenada, riesgos mitigados, cierre exitoso.",
  },
];

function SectionCases() {
  const { ref, inView } = useInView(0.01);
  return (
    <section id="casos" className="section-padding bg-bone" ref={ref}>
      <div className="container mx-auto px-4">
        <div className={`section-header ${anim(inView)}`}>
          <p className="section-label">CASOS TÍPICOS</p>
          <h2 className="section-title text-navy">Retos reales que hemos resuelto</h2>
        </div>
        <div className="space-y-6 max-w-4xl mx-auto">
          {cases.map((c, i) => (
            <div
              key={c.num}
              className={`flex items-start gap-6 p-6 rounded-xl border border-[hsl(var(--navy-deep)/.1)] hover:shadow-md transition-shadow ${anim(inView)}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="flex-shrink-0 p-3 rounded-xl bg-[hsl(var(--gold)/.1)]">
                <c.icon className="h-6 w-6 text-gold" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono-stat text-gold text-sm font-bold">Caso {c.num}</span>
                  <h3 className="font-display text-lg font-bold text-navy">{c.title}</h3>
                </div>
                <p className="font-sans text-sm text-steel leading-relaxed">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========== SECTION 6 — NUESTROS CLIENTES =========== */
const clientLogoData = [
  { name: "World Bank Group", logo: logoWorldBank },
  { name: "BID — Banco Interamericano de Desarrollo", logo: logoBID },
  { name: "UPME", logo: logoUPME },
  { name: "Transmilenio", logo: logoTransmilenio },
  { name: "Federación Nacional de Cafeteros", logo: logoFedecafe },
  { name: "Genser Power", logo: logoGenserPower },
  { name: "Sacyr Concesiones", logo: logoSacyr },
  { name: "Seguros del Estado S.A.", logo: logoSegurosEstado },
  { name: "Conconcreto", logo: logoConconcreto },
  { name: "Puerto Bahía", logo: logoPuertoBahia },
  { name: "Canacol Energy Ltd.", logo: logoCanacol },
  { name: "Consorcio Minero San Jorge", logo: logoConsorcioSanJorge },
];

function SectionTrackRecord() {
  const { ref, inView } = useInView();
  return (
    <section id="nuestros-clientes" className="section-padding bg-navy-mid" ref={ref}>
      <div className="container mx-auto px-4">
        <div className={`section-header ${anim(inView)}`}>
          <p className="section-label">NUESTROS CLIENTES</p>
          <h2 className="section-title text-bone">Nuestros Clientes</h2>
          <p className="font-sans text-bone/90 text-lg max-w-2xl mx-auto">
            Organizaciones que han confiado en la experiencia de nuestro equipo.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {clientLogoData.map((client, i) => (
            <div
              key={client.name}
              className={`bg-bone/5 rounded-lg p-5 flex items-center justify-center hover:bg-bone/10 transition-all duration-300 group ${anim(inView)}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <img
                src={client.logo}
                alt={`Logo de ${client.name}`}
                className="max-h-16 w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                loading="lazy"
                width={120}
                height={64}
              />
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-bone/70 italic mt-8 font-sans">
          Organizaciones con las que miembros del equipo SYCMA han trabajado a lo largo de su trayectoria profesional.
        </p>
      </div>
    </section>
  );
}

/* =========== SECTION 7 — CTA FINAL =========== */
function SectionCTA() {
  const { ref, inView } = useInView();
  return (
    <section className="relative overflow-hidden" ref={ref}>
      <img src={ctaHandshake} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" loading="lazy" width={1920} height={600} />
      <div className="absolute inset-0 bg-[hsl(var(--navy-deep)/.72)]" />
      <div className="absolute inset-0 grain-overlay" />
      <div className="section-padding relative z-10">
        <div className="container mx-auto px-4">
          <div className={`max-w-3xl mx-auto text-center ${anim(inView)}`}>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[2.5rem] font-bold text-bone mb-6">
              ¿Listo para tomar la decisión financiera más importante de tu empresa?
            </h2>
            <p className="font-sans text-lg text-steel mb-8 max-w-xl mx-auto">
              Agenda una sesión de diagnóstico de 30 minutos con uno de nuestros socios. Sin costo. Confidencial.
            </p>
            <Link to="/contacto" className="btn-gold text-lg px-10 py-5 rounded-lg inline-block">
              Agendar diagnóstico gratuito →
            </Link>
            <p className="font-sans text-sm text-steel/70 mt-6">
              comercial@sycmahg.com · Respondemos en menos de 24 horas
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
